/**
 * Epic 12 Phase B: Context bundle for support escalation.
 * Plain-text diagnostic summary: org/site, ops state, signals, URL.
 */

import type { OpsState } from './opsState';

export interface ContextBundleParams {
  orgId?: string;
  siteId?: string;
  page?: string;
  state: OpsState;
  reasons?: Array<{ title: string; detail?: string }>;
  userRole?: string;
  deviceId?: string;
  cameraId?: string;
  /** Page-available signals (drift, deployment health, backlog, etc.) */
  extra?: Record<string, unknown>;
  url?: string;
}

/**
 * Builds a plain-text context bundle for copy/export.
 * Used by OpsStateBanner "Copy context" and escalation flows.
 */
export function buildContextBundle(params: ContextBundleParams): string {
  const lines: string[] = [
    '[Edgentik Ops Context]',
    `state: ${params.state}`,
    `org: ${params.orgId ?? '—'}`,
    `site: ${params.siteId ?? '—'}`,
    `page: ${params.page ?? '—'}`,
    `ts: ${new Date().toISOString()}`,
    `tz: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
    `url: ${params.url ?? (typeof window !== 'undefined' ? window.location.href : '—')}`,
  ];
  if (params.userRole) lines.push(`role: ${params.userRole}`);
  if (params.deviceId) lines.push(`device_id: ${params.deviceId}`);
  if (params.cameraId) lines.push(`camera_id: ${params.cameraId}`);
  if (params.reasons?.length) {
    lines.push('');
    lines.push('reasons:');
    params.reasons.forEach((r) => lines.push(`  - ${r.title}${r.detail ? `: ${String(r.detail).slice(0, 200)}` : ''}`));
  }
  if (params.extra && Object.keys(params.extra).length > 0) {
    lines.push('');
    lines.push('signals:');
    for (const [k, v] of Object.entries(params.extra)) {
      if (v != null && v !== '') lines.push(`  ${k}: ${typeof v === 'object' ? JSON.stringify(v).slice(0, 150) : v}`);
    }
  }
  return lines.join('\n');
}
